// EventsPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

/* ============ THEME ============ */
const THEME = {
  fontFamily:
    "'Prompt', system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial, 'Noto Sans Thai', sans-serif",
  pageBg: "#0b1220",
  textOnDark: "rgba(255,255,255,.92)",
  glass: "rgba(255,255,255,.08)",
  glassStroke: "rgba(255,255,255,.18)",
  p1: "#60a5fa",
  p2: "#22d3ee",
  p3: "#34d399",
};

// วิดีโอ/ภาพ hero (เปลี่ยนเป็นไฟล์ของคุณได้)
const HERO_MEDIA = {
  video:
    "https://cdn.coverr.co/videos/coverr-reading-a-book-1261/1080p.mp4",
  image:
    "https://scandiweb.com/blog/wp-content/uploads/2020/01/ecom360_conference_hosting_successful_event.jpeg",
};

/* ============ DATA (ตัวอย่าง) ============ */
const TAGS = ["ทั้งหมด", "สัมมนา", "เวิร์กช็อป", "Live Q&A"];
const EVENTS = [
  {
    id: "e1",
    title: "Debt Collection Playbook 2025",
    category: "สัมมนา",
    startISO: nextDate(7, 10), // อีก 7 วัน เวลา 10:00
    endISO: nextDate(7, 12),
    image:
      "https://images.unsplash.com/photo-1556157382-97eda2d62296?q=80&w=1600&auto=format&fit=crop",
    location: { type: "onsite", name: "Auditorium A, Bangkok", address: "ถ.พหลโยธิน จตุจักร กรุงเทพฯ", lat: 13.826, lng: 100.56 },
    speakers: ["ทนายความ กิตติ", "CPA มณี"],
    capacity: 120,
    seatsLeft: 35,
    description:
      "สรุปกลยุทธ์ทวงหนี้เชิงระบบ: เอกสาร หลักฐาน ไทม์ไลน์ และตัวอย่างดีมานด์เลตเตอร์",
    featured: true,
  },
  {
    id: "e2",
    title: "Workshop: เขียนสัญญาให้กู้แบบฟ้องได้",
    category: "เวิร์กช็อป",
    startISO: nextDate(21, 9),
    endISO: nextDate(21, 12),
    image:
      "https://images.unsplash.com/photo-1473181488821-2d23949a045a?q=80&w=1600&auto=format&fit=crop",
    location: { type: "onsite", name: "Meeting Room 3", address: "สาทร กรุงเทพฯ", lat: 13.722, lng: 100.53 },
    speakers: ["ทนายความ ธนา"],
    capacity: 40,
    seatsLeft: 0, // เต็มแล้ว
    description:
      "ลงมือเขียนสัญญาให้กู้ยืม + ข้อตกลงดอกเบี้ย/เบี้ยปรับ แบบใช้จริงในศาล",
  },
  {
    id: "e3",
    title: "Live Q&A: หนี้สูญทำอย่างไรกับภาษี?",
    category: "Live Q&A",
    startISO: nextDate(5, 19),
    endISO: nextDate(5, 20),
    image:
      "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?q=80&w=1600&auto=format&fit=crop",
    location: { type: "online", url: "https://zoom.example.com/abc" },
    speakers: ["CPA วนิดา"],
    capacity: 1000,
    seatsLeft: 1000,
    description:
      "ถามสดเรื่องภาษีหนี้สูญ/เอกสารยอมรับของสรรพากร เตรียมคำถามล่วงหน้าได้",
  },
  {
    id: "e4",
    title: "Case Study Night: ฟ้องทวงหนี้ให้ชนะ",
    category: "สัมมนา",
    startISO: prevDate(10, 18),
    endISO: prevDate(10, 20),
    image:
      "https://images.unsplash.com/photo-1519681393784-d120267933ba?q=80&w=1600&auto=format&fit=crop",
    location: { type: "online", url: "https://youtube.com/" },
    speakers: ["ทีม Litigation"],
    capacity: 500,
    seatsLeft: 0,
    description: "รีวิวคดีจริงตั้งแต่รวบรวมหลักฐานจนถึงคำพิพากษา — วิดีโอย้อนหลัง",
  },
];

// helpers to create dates relative to now (local TZ)
function nextDate(daysFromNow, hour = 10) {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  d.setHours(hour, 0, 0, 0);
  return d.toISOString();
}
function prevDate(daysBefore, hour = 10) {
  const d = new Date();
  d.setDate(d.getDate() - daysBefore);
  d.setHours(hour, 0, 0, 0);
  return d.toISOString();
}

/* ============ PAGE ============ */
export default function EventsPage() {
  const heroRef = useRef(null);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("ทั้งหมด");
  const [month, setMonth] = useState("all");
  const [activeModal, setActiveModal] = useState(null); // event object

  // Parallax + reveal
  useEffect(() => {
    const onScroll = () => {
      const h = heroRef.current;
      if (!h) return;
      const y = window.scrollY;
      h.style.setProperty("--parallax", Math.min(y * 0.22, 120) + "px");
      h.style.setProperty("--blur", Math.min(y / 70, 8) + "px");
      h.style.setProperty("--fade", Math.min(y / 420, 0.5));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("reveal-in")),
      { threshold: 0.15 }
    );
    document.querySelectorAll(".reveal").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Featured = งานที่กำลังจะถึงเร็วสุด (ยังไม่จบ)
  const now = new Date();
  const upcomingSorted = [...EVENTS].sort((a, b) => new Date(a.startISO) - new Date(b.startISO));
  const featured = upcomingSorted.find((e) => new Date(e.endISO) > now) || upcomingSorted[0];

  // Filtered list
  const list = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return EVENTS.filter((e) => {
      const matchQ =
        !kw ||
        e.title.toLowerCase().includes(kw) ||
        e.description.toLowerCase().includes(kw) ||
        e.category.toLowerCase().includes(kw);
      const matchTag = tag === "ทั้งหมด" || e.category === tag;
      const matchMonth =
        month === "all" ||
        new Date(e.startISO).getMonth() + 1 === Number(month);
      return matchQ && matchTag && matchMonth;
    }).sort((a, b) => new Date(a.startISO) - new Date(b.startISO));
  }, [q, tag, month]);

  // Months for filter (มีเฉพาะเดือนที่มีงาน)
  const monthOptions = useMemo(() => {
    const set = new Set();
    EVENTS.forEach((e) => set.add(new Date(e.startISO).getMonth() + 1));
    return Array.from(set).sort((a, b) => a - b);
  }, []);

  return (
    <div className="events-page" style={{ fontFamily: THEME.fontFamily, marginBottom: "-60px" }}>
      <Style />

      {/* HERO */}
      <header className="hero" ref={heroRef} >
        {HERO_MEDIA.video ? (
          <video className="hero-media" autoPlay muted loop playsInline poster={HERO_MEDIA.image}>
            <source src={HERO_MEDIA.video} type="video/mp4" />
          </video>
        ) : (
          <img className="hero-media" src={HERO_MEDIA.image} alt="" />
        )}
        <div className="hero-overlay" />
        <div className="hero-content">
          <nav className="breadcrumbs">
            <a href="/">หน้าแรก</a> <span>•</span> <span aria-current="page">อีเวนต์</span>
          </nav>
          <h1 className="hero-title">อีเวนต์ &amp; สัมมนา</h1>
          <p className="hero-sub">
            เวิร์กช็อป/สัมมนาเชิงลึกด้าน <b>ทวงหนี้</b> <b>สมรส/การหย่า</b> และ <b>ภาษี</b> —
            เลือกเข้าร่วมแบบ Onsite หรือ Online
          </p>
          <div className="hero-quick reveal">
            {TAGS.filter((t) => t !== "ทั้งหมด").map((t) => (
              <button key={t} className="pill" onClick={() => setTag(t)}>
                {t}
              </button>
            ))}
          </div>
        </div>
        <div className="hero-bottom-fade" />
      </header>

      {/* TOOLBAR */}
      <section className="toolbar reveal">
        <div className="toolbar-inner">
          <div className="search">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <circle cx="11" cy="11" r="7" stroke="currentColor" fill="none" />
              <path d="M20 20l-3.5-3.5" stroke="currentColor" />
            </svg>
            <input
              placeholder="ค้นหาอีเวนต์… (เช่น เวิร์กช็อป, ภาษี, ทวงหนี้)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="tags">
            {TAGS.map((t) => (
              <button
                key={t}
                className={`chip ${tag === t ? "active" : ""}`}
                onClick={() => setTag(t)}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="month">
            <label>เดือน:</label>
            <select value={month} onChange={(e) => setMonth(e.target.value)}>
              <option value="all">ทั้งหมด</option>
              {monthOptions.map((m) => (
                <option key={m} value={m}>
                  {thaiMonth(m)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* FEATURED + COUNTDOWN */}
      {!!featured && (
        <section className="featured reveal">
          <FeaturedEvent ev={featured} onRegister={() => setActiveModal(featured)} />
        </section>
      )}

      {/* GRID */}
      <section className="grid">
        {list.map((e) => (
          <EventCard key={e.id} ev={e} onRegister={() => setActiveModal(e)} />
        ))}
        {list.length === 0 && (
          <div className="empty">ไม่มีอีเวนต์ตามเงื่อนไขที่เลือก</div>
        )}
      </section>

      {/* TIMELINE BY MONTH */}
      <section className="timeline reveal">
        <h3>กำหนดการตามเดือน</h3>
        <div className="rail">
          {groupByMonth(EVENTS).map(([key, items]) => (
            <div className="rail-month" key={key}>
              <div className="rail-head">{key}</div>
              <div className="rail-items">
                {items.map((e) => (
                  <a key={e.id} href={`#${e.id}`} className="rail-chip">
                    <span className={`dot ${statusOf(e)}`} />
                    {formatDay(e.startISO)} — {e.title}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MODAL REGISTER */}
      {activeModal && (
        <RegisterModal ev={activeModal} onClose={() => setActiveModal(null)} />
      )}

      {/* CTA */}
      <section className="final-cta reveal">
        <h2>อยากจัดอีเวนต์ In-house ให้ทีมของคุณ?</h2>
        <p>เรามีคอร์สปรับแต่งเฉพาะองค์กร ทั้งกฎหมายทวงหนี้ การเจรจา และภาษี</p>
        <div className="cta-row">
          <a className="btn big" href="/contact">ติดต่อทีมงาน</a>
          <a className="btn ghost big" href="/articles">อ่านบทความที่เกี่ยวข้อง</a>
        </div>
      </section>
    </div>
  );
}

/* ============ COMPONENTS ============ */
function FeaturedEvent({ ev, onRegister }) {
  const [left, setLeft] = useState(getLeft(ev));
  useEffect(() => {
    const t = setInterval(() => setLeft(getLeft(ev)), 1000);
    return () => clearInterval(t);
  }, [ev]);
  const st = statusOf(ev);

  return (
    <div className="feat">
      <img src={ev.image} alt="" className="feat-bg" />
      <div className="feat-grad" />
      <div className="feat-body">
        <div className="kicker">กำลังจะเริ่ม</div>
        <h2 className="title">{ev.title}</h2>
        <p className="desc">{ev.description}</p>

        <div className="meta">
          <span className="badge">{ev.category}</span>
          <span>{formatDateRange(ev.startISO, ev.endISO)}</span>
          <span>•</span>
          <span>{locationText(ev.location)}</span>
        </div>

        <div className="countdown">
          {left.totalMs > 0 ? (
            <>
              <TimeBox label="วัน" value={left.d} />
              <TimeBox label="ชม." value={left.h} />
              <TimeBox label="นาที" value={left.m} />
              <TimeBox label="วินาที" value={left.s} />
            </>
          ) : (
            <span className="live">{st === "live" ? "กำลังถ่ายทอดสด" : "เริ่มแล้ว"}</span>
          )}
        </div>

        <div className="actions">
          <button className="btn" onClick={onRegister} disabled={st !== "upcoming" || ev.seatsLeft === 0}>
            {ev.seatsLeft === 0 ? "เต็มแล้ว" : "ลงทะเบียน"}
          </button>
          <a className="btn ghost" href={googleCalLink(ev)} target="_blank" rel="noreferrer">
            เพิ่มลง Google Calendar
          </a>
          <a className="btn ghost" href={icsDataUrl(ev)} download={`${ev.title}.ics`}>
            ดาวน์โหลด .ics
          </a>
        </div>
      </div>
    </div>
  );
}

function EventCard({ ev, onRegister }) {
  const st = statusOf(ev);
  return (
    <a id={ev.id} className="card reveal" href={"#"+ev.id} onClick={(e)=>e.preventDefault()}>
      <div className="cover">
        <img src={ev.image} alt="" loading="lazy" />
        <div className="overlay" />
        <span className={`state ${st}`}>{stateLabel(st, ev)}</span>
        <span className="badge">{ev.category}</span>
      </div>
      <div className="body">
        <h3 className="title">{ev.title}</h3>
        <div className="meta">
          <span>{formatDateRange(ev.startISO, ev.endISO)}</span>
          <span>•</span>
          <span>{locationText(ev.location)}</span>
        </div>
        <p className="desc">{ev.description}</p>
        <div className="footer">
          <div className="speakers">{ev.speakers.join(" • ")}</div>
          <div className="">
            <button
              className="btn small"
              onClick={(e) => {
                e.preventDefault();
                onRegister();
              }}
              disabled={st !== "upcoming" || ev.seatsLeft === 0}
            >
              {ev.seatsLeft === 0 ? "จบแล้ว" : "ลงทะเบียน"}
            </button>
          </div>
        </div>
      </div>
    </a>
  );
}

function RegisterModal({ ev, onClose }) {
  const [ok, setOk] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", seats: 1 });
  const st = statusOf(ev);

  const submit = (e) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(form.email) || !form.name.trim()) return;
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setOk(true);
    }, 900);
  };

  return (
    <div className="modal-wrap" role="dialog" aria-modal="true">
      <div className="modal">
        <button className="close" onClick={onClose} aria-label="ปิด">×</button>

        {!ok ? (
          <>
            <div className="m-head">
              <img src={ev.image} alt="" />
              <div>
                <div className="kicker">{ev.category}</div>
                <h3>{ev.title}</h3>
                <div className="meta">
                  {formatDateRange(ev.startISO, ev.endISO)} • {locationText(ev.location)}
                </div>
              </div>
            </div>

            {st !== "upcoming" || ev.seatsLeft === 0 ? (
              <div className="note">อีเวนต์นี้ไม่เปิดลงทะเบียนแล้ว</div>
            ) : (
              <form onSubmit={submit} className="form">
                <div className="grid">
                  <Field
                    label="ชื่อ–นามสกุล"
                    name="name"
                    value={form.name}
                    onChange={(v) => setForm({ ...form, name: v })}
                    required
                  />
                  <Field
                    label="อีเมล"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={(v) => setForm({ ...form, email: v })}
                    required
                  />
                </div>
                <div className="grid">
                  <Select
                    label="จำนวนผู้เข้าร่วม"
                    name="seats"
                    value={form.seats}
                    onChange={(v) => setForm({ ...form, seats: Number(v) })}
                    options={[1, 2, 3, 4, 5]}
                  />
                  <Field
                    label="ช่องทางติดต่อเพิ่มเติม (ไม่บังคับ)"
                    name="phone"
                    value={form.phone || ""}
                    onChange={(v) => setForm({ ...form, phone: v })}
                  />
                </div>
                <button className="btn" disabled={sending}>
                  {sending ? "กำลังส่ง..." : "ยืนยันการลงทะเบียน"}
                </button>
              </form>
            )}
          </>
        ) : (
          <div className="ok">
            <div className="ok-ic">✓</div>
            <h3>ลงทะเบียนเรียบร้อย</h3>
            <p>
              เราส่งอีเมลยืนยันไปที่ <b>{form.email}</b> แล้ว
              {ev.location.type === "online" ? (
                <> • ลิงก์เข้าร่วม: <a href={ev.location.url} target="_blank" rel="noreferrer">{ev.location.url}</a></>
              ) : (
                <> • สถานที่: <b>{ev.location.name}</b> ({ev.location.address})</>
              )}
            </p>
            <div className="row">
              <a className="btn" href={googleCalLink(ev)} target="_blank" rel="noreferrer">เพิ่มใน Google Calendar</a>
              <a className="btn ghost" href={icsDataUrl(ev)} download={`${ev.title}.ics`}>ดาวน์โหลด .ics</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required }) {
  return (
    <div className="field">
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder=" "
        required={required}
      />
      <label htmlFor={name}>{label}</label>
    </div>
  );
}
function Select({ label, name, value, onChange, options }) {
  return (
    <div className="field">
      <select id={name} name={name} value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
      <label htmlFor={name}>{label}</label>
    </div>
  );
}
function TimeBox({ label, value }) {
  return (
    <div className="timebox">
      <div className="num">{String(value).padStart(2, "0")}</div>
      <div className="lab">{label}</div>
    </div>
  );
}

/* ============ UTILS ============ */
function statusOf(e) {
  const now = new Date();
  const s = new Date(e.startISO);
  const ed = new Date(e.endISO);
  if (now < s) return "upcoming";
  if (now >= s && now <= ed) return "live";
  return "ended";
}
function stateLabel(st, ev) {
  if (ev.seatsLeft === 0 && st === "upcoming") return "เต็มแล้ว";
  return st === "upcoming" ? "กำลังจะเริ่ม" : st === "live" ? "ถ่ายทอดสด" : "จบแล้ว";
}
function formatDateRange(startISO, endISO) {
  const s = new Date(startISO);
  const e = new Date(endISO);
  const sameDay = s.toDateString() === e.toDateString();
  const ds = s.toLocaleDateString("th-TH", { year: "numeric", month: "short", day: "numeric" });
  const ts = s.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" });
  const te = e.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" });
  return sameDay ? `${ds} • ${ts}–${te}` : `${ds} ${ts} → ${e.toLocaleDateString("th-TH", { year: "numeric", month: "short", day: "numeric" })} ${te}`;
}
function formatDay(iso){ const d=new Date(iso); return d.toLocaleDateString('th-TH',{day:'2-digit',month:'short'}); }
function thaiMonth(m){ return new Date(2025, m-1, 1).toLocaleDateString('th-TH',{month:'long'}); }
function locationText(loc) {
  return loc.type === "online" ? "ออนไลน์" : `${loc.name}`;
}
function getLeft(ev) {
  const t = Math.max(0, new Date(ev.startISO) - new Date());
  const d = Math.floor(t / (1000 * 60 * 60 * 24));
  const h = Math.floor((t / (1000 * 60 * 60)) % 24);
  const m = Math.floor((t / (1000 * 60)) % 60);
  const s = Math.floor((t / 1000) % 60);
  return { totalMs: t, d, h, m, s };
}
function icsDataUrl(ev) {
  const dt = (iso) =>
    new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
  const loc =
    ev.location.type === "online"
      ? ev.location.url
      : `${ev.location.name} ${ev.location.address || ""}`;
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//ThaiTaxLaw//Events//TH",
    "BEGIN:VEVENT",
    `UID:${ev.id}@thaitaxlaw`,
    `DTSTAMP:${dt(new Date().toISOString())}`,
    `DTSTART:${dt(ev.startISO)}`,
    `DTEND:${dt(ev.endISO)}`,
    `SUMMARY:${escapeICS(ev.title)}`,
    `DESCRIPTION:${escapeICS(ev.description || "")}`,
    `LOCATION:${escapeICS(loc)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  return "data:text/calendar;charset=utf-8," + encodeURIComponent(ics);
}
function escapeICS(s) {
  return String(s).replace(/([,;])/g, "\\$1").replace(/\n/g, "\\n");
}
function googleCalLink(ev) {
  const fmt = (iso) =>
    new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
  const text = encodeURIComponent(ev.title);
  const details = encodeURIComponent(ev.description || "");
  const location =
    ev.location.type === "online"
      ? encodeURIComponent(ev.location.url)
      : encodeURIComponent(`${ev.location.name} ${ev.location.address || ""}`);
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${fmt(
    ev.startISO
  )}/${fmt(ev.endISO)}&details=${details}&location=${location}`;
}
function groupByMonth(items) {
  const map = new Map();
  items
    .sort((a, b) => new Date(a.startISO) - new Date(b.startISO))
    .forEach((e) => {
      const d = new Date(e.startISO);
      const key = d.toLocaleDateString("th-TH", { month: "long", year: "numeric" });
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    });
  return Array.from(map.entries());
}

/* ============ STYLES ============ */
function Style() {
  return (
    <style>{`
:root{
  --bg:${THEME.pageBg}; --text:${THEME.textOnDark};
  --glass:${THEME.glass}; --glass-stroke:${THEME.glassStroke};
  --p1:${THEME.p1}; --p2:${THEME.p2}; --p3:${THEME.p3};
}
*{box-sizing:border-box}
.events-page{
  color:var(--text);
  background:
    radial-gradient(1200px 600px at 10% -10%, rgba(14,165,233,.12), transparent 60%),
    radial-gradient(1200px 600px at 90% 10%, rgba(99,102,241,.12), transparent 60%),
    var(--bg);
  min-height:100vh;
}

/* HERO (เหมือนหน้าแรก) */
.hero{position:relative;height:58vh;overflow:hidden;isolation:isolate;--parallax:0px;--blur:0px;--fade:0}
.hero-media{position:absolute;inset:-10% -10% -10% -10%;width:120%;height:120%;object-fit:cover;transform:translateY(var(--parallax));filter:blur(var(--blur)) brightness(.85) saturate(.95)}
.hero-overlay{position:absolute;inset:0;background:
  radial-gradient(700px 380px at 18% 32%, rgba(0,0,0,.45), rgba(0,0,0,.7)),
  linear-gradient(90deg, rgba(0,0,0,.6), rgba(0,0,0,.25), rgba(0,0,0,.6));opacity:calc(1 - var(--fade)*.35)}
.hero-bottom-fade{position:absolute;left:0;right:0;bottom:-1px;height:120px;background:linear-gradient(to bottom, transparent, var(--bg));z-index:2}
.hero-content{position:relative;z-index:3;height:100%;display:grid;align-content:center;gap:14px;padding: clamp(20px,5vw,64px);max-width:1100px;margin:0 auto}
.breadcrumbs{opacity:.9;font-size:.95rem}
.breadcrumbs a{color:rgba(255,255,255,.9);text-decoration:none}
.breadcrumbs span{opacity:.6;margin:0 6px}
.hero-title{font-size: clamp(2rem, 4.2vw, 3.6rem);line-height:1.1;margin:0;font-weight:800}
.hero-sub{max-width:60ch;opacity:.98}
.hero-quick{display:flex;gap:10px;flex-wrap:wrap}
.pill{padding:10px 14px;border-radius:999px;background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.22);backdrop-filter: blur(8px);color:#fff;text-decoration:none}

/* TOOLBAR */
.toolbar{margin-top:-36px;position:relative;z-index:4}
.toolbar-inner{max-width:1200px;margin:0 auto;display:grid;gap:14px;padding:0 clamp(16px,4vw,24px);grid-template-columns:1.2fr 2fr auto}
.search{display:flex;align-items:center;gap:10px;background:var(--glass);border:1px solid var(--glass-stroke);padding:12px 14px;border-radius:14px;backdrop-filter: blur(8px)}
.search input{background:transparent;border:none;outline:none;color:var(--text);width:100%}
.tags{display:flex;gap:10px;flex-wrap:wrap}
.chip{padding:10px 14px;border-radius:999px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.06);color:#fff;cursor:pointer;transition: transform .15s ease, background .2s ease}
.chip:hover{transform: translateY(-1px);background:rgba(255,255,255,.1)}
.chip.active{background:linear-gradient(90deg, rgba(96,165,250,.35), rgba(34,211,238,.35))}
.month{display:flex;align-items:center;gap:8px;justify-self:end}
.month select{appearance:none;padding:10px 14px;border-radius:12px;border:1px solid var(--glass-stroke);background:var(--glass);color:#fff}

/* FEATURED */
.featured{max-width:1200px;margin:18px auto 0;padding:0 clamp(16px,4vw,24px)}
.feat{position:relative;overflow:hidden;border-radius:20px;border:1px solid rgba(255,255,255,.12);isolation:isolate}
.feat-bg{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;filter:brightness(.9) saturate(.95);transform:scale(1.02)}
.feat-grad{position:absolute;inset:0;background:linear-gradient(180deg, rgba(0,0,0,.0), rgba(0,0,0,.7) 60%, rgba(0,0,0,.85))}
.feat-body{position:relative;z-index:2;padding:18px;display:grid;gap:10px;min-height:260px;align-content:end}
.feat .kicker{opacity:.9}
.feat .title{font-weight:900;margin:0;font-size: clamp(1.2rem, 2.6vw, 1.8rem)}
.feat .desc{opacity:.95;max-width:70ch}
.feat .meta{opacity:.9;display:flex;gap:8px;flex-wrap:wrap}
.badge{display:inline-block;padding:6px 10px;border-radius:999px;background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.28)}
.countdown{display:flex;gap:8px;align-items:center;flex-wrap:wrap}
.timebox{background:rgba(255,255,255,.1);border:1px solid rgba(255,255,255,.22);backdrop-filter: blur(6px);border-radius:12px;padding:6px 10px;text-align:center;min-width:64px}
.timebox .num{font-weight:900;font-variant-numeric: tabular-nums}
.timebox .lab{font-size:.85rem;opacity:.9}
.countdown .live{padding:8px 12px;border-radius:12px;background:rgba(34,197,94,.25);border:1px solid rgba(255,255,255,.2)}
.actions{display:flex;gap:10px;flex-wrap:wrap}
.btn{display:inline-flex;align-items:center;gap:10px;padding:12px 16px;border-radius:999px;color:#fff;text-decoration:none;cursor:pointer;background:linear-gradient(90deg,var(--p1),var(--p2),var(--p3));border:1px solid rgba(255,255,255,.25);transition: transform .18s ease, filter .18s ease}
.btn:hover{transform: translateY(-1px);filter: brightness(1.05)}
.btn.ghost{background:rgba(255,255,255,.08)}
.btn.small{padding:8px 12px;border-radius:14px}

/* GRID */
.grid{max-width:1200px;margin:18px auto 30px;padding:0 clamp(16px,4vw,24px);display:grid;gap:16px;grid-template-columns: repeat(3, minmax(0,1fr))}
.card{position:relative;overflow:hidden;border-radius:18px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);text-decoration:none;color:#fff;display:flex;flex-direction:column;transition: transform .18s ease, box-shadow .18s ease}
.card:hover{transform: translateY(-3px);box-shadow:0 10px 30px rgba(0,0,0,.35)}
.card .cover{position:relative;aspect-ratio: 16/9}
.card .cover img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;transform:scale(1.02);transition: transform .6s ease;filter:brightness(.95) saturate(.95)}
.card:hover .cover img{transform:scale(1.06)}
.card .overlay{position:absolute;inset:0;background:linear-gradient(180deg, rgba(0,0,0,.0), rgba(0,0,0,.75))}
.card .state{position:absolute;left:12px;top:12px;padding:6px 10px;border-radius:999px;background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.28)}
.card .state.live{background:rgba(34,197,94,.25)}
.card .state.ended{background:rgba(248,113,113,.25)}
.card .badge{position:absolute;right:12px;top:12px}
.card .body{display:grid;gap:8px;padding:14px}
.card .title{margin:0;font-size:1.1rem;font-weight:900}
.card .meta{opacity:.9;display:flex;gap:8px;flex-wrap:wrap;font-size:.95rem}
.card .desc{opacity:.95}
.card .footer{display:flex;align-items:center;justify-content:space-between;margin-top:2px}
.card .speakers{opacity:.85}

/* TIMELINE */
.timeline{max-width:1200px;margin:6px auto 40px;padding:0 clamp(16px,4vw,24px)}
.timeline h3{margin:0 0 8px}
.rail{display:flex;gap:16px;overflow:auto;padding-bottom:6px}
.rail-month{flex:0 0 auto;min-width:280px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:14px;padding:10px}
.rail-head{font-weight:900;margin-bottom:6px}
.rail-items{display:grid;gap:6px}
.rail-chip{display:flex;gap:8px;align-items:center;text-decoration:none;color:#fff;opacity:.95;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);padding:8px;border-radius:10px}
.rail-chip:hover{background:rgba(255,255,255,.1)}
.dot{width:8px;height:8px;border-radius:999px;background:#aaa}
.dot.upcoming{background:#60a5fa}
.dot.live{background:#22c55e}
.dot.ended{background:#f87171}

/* MODAL */
.modal-wrap{position:fixed;inset:0;background:rgba(2,6,23,.6);backdrop-filter: blur(6px);display:grid;place-items:center;z-index:60}
.modal{background:linear-gradient(180deg, rgba(96,165,250,.18), rgba(255,255,255,.06));border:1px solid var(--glass-stroke);border-radius:18px;padding:16px;max-width:720px;width:92%}
.close{position:absolute;right:18px;top:10px;background:transparent;border:none;color:#fff;font-size:24px;cursor:pointer}
.m-head{display:flex;gap:12px;margin-bottom:8px}
.m-head img{width:120px;height:90px;object-fit:cover;border-radius:12px}
.m-head .kicker{opacity:.9}
.form .grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.field{position:relative}
.field input,.field select{width:100%;padding:12px;border-radius:12px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.06);color:#fff;outline:none}
.field label{position:absolute;left:10px;top:50%;transform:translateY(-50%);opacity:.85;pointer-events:none;transition: all .15s}
.field input:not(:placeholder-shown)+label,.field input:focus+label,.field select:focus+label,.field select:valid+label{top:-8px;font-size:.8rem;background:rgba(11,18,32,.9);padding:0 6px}
.ok{text-align:center}
.ok-ic{width:54px;height:54px;border-radius:50%;display:grid;place-items:center;background:rgba(34,197,94,.25);border:1px solid rgba(255,255,255,.25);font-size:1.4rem;margin:8px auto}
.ok .row{display:flex;gap:10px;justify-content:center}

/* CTA */
.final-cta{max-width:1000px;margin:0 auto 60px;padding:0 clamp(16px,4vw,24px);text-align:center}
.final-cta h2{font-size:1.6rem;margin:8px 0}
.cta-row{display:flex;gap:12px;justify-content:center;flex-wrap:wrap}

/* REVEAL */
.reveal{opacity:0;transform: translateY(14px) scale(.98)}
.reveal-in{animation: rise .6s cubic-bezier(.2,.7,.2,1) forwards}
@keyframes rise{to{opacity:1;transform: translateY(0) scale(1)}}

/* RESPONSIVE */
@media (max-width: 1024px){
  .grid{grid-template-columns:repeat(2, minmax(0,1fr))}
  .m-head{flex-direction:column;align-items:flex-start}
}
@media (max-width:640px){
  .hero{height:52vh}
  .grid{grid-template-columns:1fr}
  .form .grid{grid-template-columns:1fr}
}
    `}</style>
  );
}
